// src/pages/PftMergeRows.js
//
// The side-by-side rows of a PFT merge (pftMergePlan.js): profile fields, then each
// PFT date. A field that differs gets "Keep RegX" / "Use PFT" (Keep is the default).
// onlyDifferences: Move all's review shows just the fields that differ; the single-card
// Move shows everything.
//
// When something differs (2026-10-19), the person can check both sources before
// choosing: "Keep RegX" is what the patient's record says now (link to their patient
// page), "Use PFT" is what the PDF says (opens the PDF; a differing PFT-date value links
// that date's own PDF).

import { useState } from "react";
import { ExternalLink } from "lucide-react";
import { PFT_TEST_FIELDS } from "../services/pftTests";
import { diagnosisLabel } from "../constants/diseaseOptions";
import { openPftPdf } from "../services/pftPdfService";

const testLabel = (k) => (PFT_TEST_FIELDS.find((f) => f.key === k) || {}).label || k;
const KIND = {
  same: { text: "same", cls: "text-slate-400" },
  fill: { text: "will be filled", cls: "text-emerald-200" },
  conflict: { text: "different: pick one", cls: "text-amber-200" },
  keep: { text: "nothing in the PFT", cls: "text-slate-500" },
};
const linkCls = "inline-flex items-center gap-1 text-sky-300 hover:underline";

/** choices after picking `value` ("current" | "incoming") for one field. */
export const withChoice = (c, scope, key, field, value) =>
  scope === "profile"
    ? { ...c, profile: { ...c.profile, [field]: value } }
    : { ...c, tests: { ...c.tests, [key]: { ...(c.tests[key] || {}), [field]: value } } };

/** The PDFs a card's values come from, each once. */
export const pdfsOfCard = (card) => {
  const seen = new Set();
  return ((card && card.pendingImports) || [])
    .filter((i) => i.file && !seen.has(i.file) && seen.add(i.file))
    .map((i) => ({ file: i.file, fileName: i.fileName || i.file.split("/").pop() }));
};

// regxHref: the patient page of the record being merged into. pdfs: [{file, fileName}].
export default function PftMergeRows({ plan, choices, setChoice, onlyDifferences = false, regxHref, pdfs = [] }) {
  const [pdfError, setPdfError] = useState(null);

  const openPdf = async (file) => {
    setPdfError(null);
    try {
      await openPftPdf(file);
    } catch (e) {
      setPdfError(e.message);
    }
  };

  const pdfLink = (file, text, key) => (
    <button key={key || file} type="button" onClick={() => openPdf(file)} className={linkCls}>
      {text} <ExternalLink className="w-3 h-3" />
    </button>
  );

  // Health survey (2026-10-23): rows the patient answered themselves offer "Use survey",
  // ticked by default; the rest of what the survey brings is listed under the rows.
  const surveyRows = plan.profile.some((r) => r.source === "survey" && r.kind === "conflict");
  const fromSurvey = plan.fromSurvey && (
    <div className="rounded-lg border p-2 text-[12px] space-y-0.5 pft-survey-answers">
      <div className="font-semibold">Also from the patient's health survey</div>
      <div>
        Diagnosis: {plan.fromSurvey.addDiagnosis.length ? `adds ${plan.fromSurvey.addDiagnosis.map(diagnosisLabel).join(", ")}` : "nothing new"} (none removed)
      </div>
      <div>
        Signed consent: listed under Other files
        {plan.fromSurvey.fillsConsent ? ", and becomes their consent file (they have none)" : " (their current consent file is kept)"}
      </div>
      <div>Medical history: added to the notes</div>
    </div>
  );

  // Where each choice comes from, with a link to check it. Shown only when something differs.
  const sources = plan.conflicts > 0 && (
    <div className="rounded-lg border border-white/10 bg-white/[0.03] p-2 text-[12px] text-slate-300 space-y-0.5">
      <div>
        <span className="font-semibold text-slate-100">Keep RegX</span> = on the patient now
        {regxHref && (
          <>
            {" · "}
            <a href={regxHref} target="_blank" rel="noopener noreferrer" className={linkCls}>
              Open patient <ExternalLink className="w-3 h-3" />
            </a>
          </>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-x-2">
        <span>
          <span className="font-semibold text-slate-100">Use PFT</span> = from the PDF
        </span>
        {pdfs.map((p, i) => (
          <span key={p.file}>
            {"· "}
            {pdfLink(p.file, pdfs.length > 1 ? `Open PDF ${i + 1}` : "Open PDF")}
          </span>
        ))}
      </div>
      {surveyRows && (
        <div>
          <span className="font-semibold text-slate-100">Use survey</span> = what the patient typed on the health survey (ticked unless you keep RegX)
        </div>
      )}
      {pdfError && <div className="text-amber-200">{pdfError}</div>}
    </div>
  );

  // row: the profile row, for a survey answer's wording and default ("Use survey", ticked).
  const choiceRadios = (scope, key, field, current, incoming, row = null) => {
    const val = scope === "profile" ? choices.profile[field] : (choices.tests[key] || {})[field];
    const fromSurvey = row && row.source === "survey";
    return (
      <span className="flex flex-wrap gap-3">
        {[["current", `Keep RegX: ${current}`], ["incoming", `${fromSurvey ? "Use survey" : "Use PFT"}: ${incoming}`]].map(([v, text]) => (
          <label key={v} className="inline-flex items-center gap-1 text-[12px] text-slate-100">
            <input
              type="radio"
              name={`${scope}-${key}-${field}`}
              checked={(val || (row && row.defaultChoice) || "current") === v}
              onChange={() => setChoice(scope, key, field, v)}
              className="accent-purple-500"
            />
            {text}
          </label>
        ))}
      </span>
    );
  };

  if (onlyDifferences) {
    const profile = plan.profile.filter((row) => row.kind === "conflict");
    const tests = plan.tests
      .map((t) => ({ t, fields: (t.fields || []).filter((f) => f.kind === "conflict") }))
      .filter((x) => x.fields.length);
    return (
      <div className="space-y-1.5">
        {sources}
        {profile.map((row) => (
          <div key={row.field} className="text-[12px] grid grid-cols-1 sm:grid-cols-[9rem_1fr] gap-1">
            <span className="text-slate-400">{row.label}</span>
            {choiceRadios("profile", "", row.field, row.current, row.incoming, row)}
          </div>
        ))}
        {tests.map(({ t, fields }) =>
          fields.map((f) => (
            <div key={`${t.incoming.id}-${f.field}`} className="text-[12px] grid grid-cols-1 sm:grid-cols-[9rem_1fr] gap-1">
              <span className="text-slate-400">
                PFT {t.incoming.date || "(no date)"}: {testLabel(f.field)}
                {t.incoming.file && pdfs.length > 1 && <span className="block">{pdfLink(t.incoming.file, "This PDF", `${t.incoming.id}-${f.field}`)}</span>}
              </span>
              {choiceRadios("tests", t.incoming.id, f.field, f.current, f.incoming)}
            </div>
          )),
        )}
        {fromSurvey}
      </div>
    );
  }

  return (
    <>
      {sources}
      <div className="space-y-1.5">
        {plan.profile.map((row) => (
          <div key={row.field} className="text-[12px] grid grid-cols-1 sm:grid-cols-[9rem_1fr] gap-1">
            <span className="text-slate-400">{row.label}</span>
            {row.kind === "conflict" ? (
              choiceRadios("profile", "", row.field, row.current, row.incoming, row)
            ) : (
              <span className="text-slate-200">
                {row.kind === "fill" ? row.incoming : row.current || "—"}{" "}
                <span className={KIND[row.kind].cls}>
                  ({KIND[row.kind].text}
                  {row.kind === "fill" && row.source === "survey" ? " from the survey" : ""})
                </span>
              </span>
            )}
          </div>
        ))}
      </div>
      <div className="space-y-2">
        {plan.tests.map((t) => (
          <div key={t.incoming.id} className="rounded-lg border border-white/10 p-2 text-[12px] text-slate-200">
            PFT {t.incoming.date || "(no date)"}:{" "}
            {t.kind === "new" && <span className="text-emerald-200">new date, will be added</span>}
            {t.kind === "already" && <span className="text-slate-400">already on this patient</span>}
            {t.kind === "same-date" && (
              <>
                <span className="text-slate-300">the patient already has a PFT on this date; compared value by value</span>
                {t.incoming.file && pdfs.length > 1 && t.fields.some((f) => f.kind === "conflict") && (
                  <> · {pdfLink(t.incoming.file, "This PDF", `${t.incoming.id}-pdf`)}</>
                )}
                <div className="mt-1 space-y-1">
                  {t.fields.filter((f) => f.kind === "conflict" || f.kind === "fill").map((f) => (
                    <div key={f.field} className="grid grid-cols-1 sm:grid-cols-[12rem_1fr] gap-1">
                      <span className="text-slate-400">{testLabel(f.field)}</span>
                      {f.kind === "conflict" ? (
                        choiceRadios("tests", t.incoming.id, f.field, f.current, f.incoming)
                      ) : (
                        <span>{f.incoming} <span className="text-emerald-200">(will be filled)</span></span>
                      )}
                    </div>
                  ))}
                  {!t.fields.some((f) => f.kind === "conflict" || f.kind === "fill") && (
                    <span className="text-slate-400">nothing new in this PFT</span>
                  )}
                </div>
              </>
            )}
          </div>
        ))}
      </div>
      {fromSurvey}
    </>
  );
}
